import { useMemo, useState } from 'react';
import { formatMoney } from '@lombok/shared';
import type { Customer } from '@lombok/shared';
import { SearchInput } from '@/components/SearchInput';
import { StatusPill } from '@/components/StatusPill';
import { QueryState } from '@/components/QueryState';
import { useCustomers } from '@/data/hooks';

interface CustomerPickerProps {
  selectedId: string;
  onSelect: (customer: Customer) => void;
  /** What to say when there are no customers to pick from (defaults to the general wording). */
  emptyDescription?: string;
}

/**
 * Fast customer search + pick used by Crear pedido. Surfaces the credit/balance
 * snapshot a sales rep needs (a docs/11 priority) right on each result so they
 * pick the right account before building the cart. Client-side filter over the
 * fetched page (the same pattern as the Clientes list).
 */
export function CustomerPicker({ selectedId, onSelect, emptyDescription }: CustomerPickerProps) {
  const [search, setSearch] = useState('');
  const query = useCustomers({ limit: 100 });

  const items = query.data?.items ?? [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (c) => c.name.toLowerCase().includes(q) || c.rif.toLowerCase().includes(q),
    );
  }, [items, search]);

  return (
    <div className="flex flex-col gap-3">
      <SearchInput value={search} onChange={setSearch} placeholder="Buscar cliente por nombre o RIF…" />

      <QueryState
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        onRetry={() => void query.refetch()}
        isEmpty={filtered.length === 0}
        loadingLabel="Cargando clientes…"
        emptyTitle={search ? 'Sin coincidencias' : 'No hay clientes'}
        emptyDescription={
          search ? 'Ajusta la búsqueda.' : (emptyDescription ?? 'Aún no se han registrado clientes.')
        }
      >
        <ul className="max-h-72 divide-y divide-border overflow-y-auto rounded-md border border-border">
          {filtered.map((c) => {
            const active = c.customerId === selectedId;
            return (
              <li key={c.customerId}>
                <button
                  type="button"
                  onClick={() => onSelect(c)}
                  aria-pressed={active}
                  className={`flex w-full flex-col gap-1 p-3 text-left transition-colors ${
                    active ? 'bg-brand-primary/10' : 'hover:bg-surface-muted'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-medium text-text-primary">{c.name}</span>
                    <StatusPill kind="account" value={c.status} />
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-text-muted">
                    <span>{c.rif}</span>
                    <span>Saldo: {formatMoney(c.openBalanceMinorUsd)}</span>
                    <span>Crédito disp.: {formatMoney(c.availableCreditMinorUsd)}</span>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </QueryState>
    </div>
  );
}
